import { createContext, useContext, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { defaultSearchPath } from "../../../../../globalConstants";

const UrlHistoryContext = createContext(null);
const PAGINATION_PARAMS = new Set(["limit", "offset"]);
const STATEFUL_LIST_PATHS = new Set(["/action-items", "/providers"]);
const CLIENT_DETAIL_ROUTE = /^\/client-detail\/[^/]+(?:\/|$)/;

function hasFilters(url) {
  const searchParams = new URL(url, window.location.origin).searchParams;
  return [...searchParams.keys()].some((key) => !PAGINATION_PARAMS.has(key));
}

function getClientSearchTarget() {
  return localStorage.getItem("Search") || defaultSearchPath;
}

export function UrlHistoryProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const historyRef = useRef([]);
  const historyIndexRef = useRef(window.history.state?.idx);
  const handledNavigationRef = useRef(null);

  useEffect(() => {
    const currentUrl = location.pathname + location.search;
    const currentHistoryIndex = window.history.state?.idx;

    if (handledNavigationRef.current === currentUrl) {
      handledNavigationRef.current = null;
      historyIndexRef.current = currentHistoryIndex;
      return;
    }

    const previousUrl = historyRef.current.at(-1);
    const previousPathname = previousUrl
      ? new URL(previousUrl, window.location.origin).pathname
      : null;

    if (
      Number.isInteger(currentHistoryIndex) &&
      Number.isInteger(historyIndexRef.current) &&
      currentHistoryIndex < historyIndexRef.current
    ) {
      if (CLIENT_DETAIL_ROUTE.test(location.pathname)) {
        const previousPage = historyRef.current
          .slice()
          .reverse()
          .find(
            (url) =>
              !CLIENT_DETAIL_ROUTE.test(
                new URL(url, window.location.origin).pathname
              )
          );
        const returnTarget = previousPage || getClientSearchTarget();
        const returnIndex = historyRef.current.lastIndexOf(returnTarget);

        historyRef.current =
          returnIndex >= 0 ? historyRef.current.slice(0, returnIndex + 1) : [returnTarget];
        handledNavigationRef.current = returnTarget;
        historyIndexRef.current = currentHistoryIndex;
        navigate(returnTarget, { replace: true });
        return;
      }

      const existingIndex = historyRef.current.lastIndexOf(currentUrl);
      if (existingIndex >= 0) {
        historyRef.current = historyRef.current.slice(0, existingIndex + 1);
        historyIndexRef.current = currentHistoryIndex;
        return;
      }
    }

    if (
      STATEFUL_LIST_PATHS.has(location.pathname) &&
      previousPathname === location.pathname &&
      !new URL(previousUrl, window.location.origin).search
    ) {
      historyRef.current[historyRef.current.length - 1] = currentUrl;
      historyIndexRef.current = currentHistoryIndex;
      return;
    }

    historyRef.current.push(currentUrl);
    historyIndexRef.current = currentHistoryIndex;
  }, [location.pathname, location.search, navigate]);

  const goBack = (prevPath = true) => {
    try {
      const currentUrl = `${location.pathname}${location.search}`;
      const previousUrls = historyRef.current.slice(0, -1).reverse();
      const previousState = previousUrls.find((url) => url !== currentUrl);
      const previousFilterState = previousUrls.find(
        (url) =>
          new URL(url, window.location.origin).pathname === location.pathname &&
          hasFilters(url)
      );
      const previousPage = previousUrls.find(
        (url) => new URL(url, window.location.origin).pathname !== location.pathname
      );
      const previousUrl = prevPath
        ? location.pathname === "/providers"
          ? previousState
          : hasFilters(currentUrl)
          ? previousState
          : previousFilterState || previousPage
        : previousState;

      if (previousUrl) {
        const previousIndex = historyRef.current.lastIndexOf(previousUrl);
        historyRef.current = historyRef.current.slice(0, previousIndex + 1);
        handledNavigationRef.current = previousUrl;
        navigate(previousUrl, { replace: true });
        return true;
      }
    } catch (error) {
      console.error("Error navigating back:", error);
    }

    return false;
  };
  return (
    <UrlHistoryContext.Provider value={{ history: historyRef.current, goBack }}>
      {children}
    </UrlHistoryContext.Provider>
  );
}

export function useUrlHistory() {
  const ctx = useContext(UrlHistoryContext);
  if (!ctx) {
    throw new Error("useUrlHistory must be used within UrlHistoryProvider");
  }
  return ctx;
}
